import styled from "@emotion/styled";
import { css } from "@emotion/react";
import {
  FONT_WEIGHT,
  MOBILE_SCREEN,
  COLOR_BLUE_TEXT,
  COLOR_PRIMARY,
  COLOR_FILE_METADATA_TEXT,
  typographyStyles,
} from "../theme";

/*
 * Paragraph / body-text components — the source of truth for body/prose styling.
 * Each emits its `typographyStyles.body*` token, then `textBase` (margin reset),
 * then `textOverrides` last, so a passed prop wins by source order (no `&&`) and
 * unset props fall through to the token. Colour isn't defaulted (text inherits
 * COLOR_APP_TEXT from :root). Reuse a style on another element via `as`
 * (e.g. `<Paragraph as="span">`).
 */

interface TextProps {
  marginTop?: string;
  marginBottom?: string;
  textAlign?: React.CSSProperties["textAlign"];
  marginLeft?: string;
  marginRight?: string;
  color?: string;
  fontWeight?: number;
  opacity?: number;
}

// Reset the UA paragraph margin so spacing is layout-driven.
const textBase = `
  margin: 0;
`;

// Emitted last so a passed prop wins; each declaration emits only when set.
const textOverrides = ({
  color,
  textAlign,
  fontWeight,
  opacity,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
}: TextProps) => `
  ${color != null ? `color: ${color};` : ""}
  ${textAlign != null ? `text-align: ${textAlign};` : ""}
  ${fontWeight != null ? `font-weight: ${fontWeight};` : ""}
  ${opacity != null ? `opacity: ${opacity};` : ""}
  ${marginTop != null ? `margin-top: ${marginTop};` : ""}
  ${marginBottom != null ? `margin-bottom: ${marginBottom};` : ""}
  ${marginLeft != null ? `margin-left: ${marginLeft};` : ""}
  ${marginRight != null ? `margin-right: ${marginRight};` : ""}
`;

// Scene body scales change mobile weight/color, so explicit props must win there too.
const responsiveTextOverrides = (props: TextProps) => css`
  ${textOverrides(props)}
  ${props.color != null &&
  css`
    ${MOBILE_SCREEN} {
      color: ${props.color};
    }
  `}
  ${props.fontWeight != null &&
  css`
    ${MOBILE_SCREEN} {
      font-weight: ${props.fontWeight};
    }
  `}
`;

export const NotarizationBody = styled.p<TextProps>`
  ${typographyStyles.notarizationBody}
  ${textBase}
  ${responsiveTextOverrides}
`;

export const NotarizationIntro = styled.p<TextProps>`
  ${typographyStyles.notarizationIntro}
  ${textBase}
  ${responsiveTextOverrides}
`;

export const NotarizationUploadIntro = styled.p<TextProps>`
  ${typographyStyles.notarizationUploadIntro}
  ${textBase}
  ${responsiveTextOverrides}
`;

export const NotarizationUploadDescription = styled.p<TextProps>`
  ${typographyStyles.notarizationUploadDescription}
  ${textBase}
  ${responsiveTextOverrides}
`;

export const NotarizationUploadStatusBody = styled.p<TextProps>`
  ${typographyStyles.notarizationUploadStatusBody}
  ${textBase}
  color: ${COLOR_BLUE_TEXT};
  ${responsiveTextOverrides}
`;

export const NotarizationRequirement = styled.p<TextProps>`
  ${typographyStyles.notarizationRequirement}
  ${textBase}
  ${responsiveTextOverrides}
`;

export const NotarizationFileName = styled.p<TextProps>`
  ${typographyStyles.notarizationFileName}
  ${textBase}
  ${responsiveTextOverrides}
`;

export const NotarizationFileMetadata = styled.p<TextProps>`
  ${typographyStyles.notarizationFileMetadata}
  ${textBase}
  color: ${COLOR_FILE_METADATA_TEXT};
  ${responsiveTextOverrides}
`;

export const NotarizationDropZoneLabel = styled.p<TextProps>`
  ${typographyStyles.notarizationDropZoneLabel}
  ${textBase}
  ${responsiveTextOverrides}
`;

export const NotarizationSafekeepingText = styled.p<TextProps>`
  ${typographyStyles.notarizationSafekeepingText}
  ${textBase}
  ${responsiveTextOverrides}
`;

export const NotarizationReminderBody = styled.p<TextProps>`
  ${typographyStyles.notarizationReminderBody}
  ${textBase}
  ${responsiveTextOverrides}
`;

export const NotarizationProgressBody = styled.p<TextProps>`
  ${typographyStyles.notarizationProgressBody}
  ${textBase}
  color: ${COLOR_PRIMARY};
  ${responsiveTextOverrides}
`;

// --- Body (regular weight) --------------------------------------------------

/** Default body copy — Figma "Body Medium" (15px / 300 / 160%). */
export const Paragraph = styled.p<TextProps>`
  ${typographyStyles.bodyMedium}
  ${textBase}
  ${textOverrides}
`;

/** Pre-purchase introduction — 16px semibold desktop, 15px medium mobile. */
export const PrePurchaseDashboardDescription = styled.p<TextProps>`
  ${typographyStyles.prePurchaseDashboardDescription}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Immigration notice body — 17px semibold, 160% line height. */
export const ImmigrationModalBody = styled.p<TextProps>`
  ${typographyStyles.immigrationModalBody}
  ${textBase}
  ${textOverrides}
`;

/** Figma "Body Extra-Small" (11px / 300 / 140%). */
export const ParagraphXSmall = styled.p<TextProps>`
  ${typographyStyles.bodyXSmall}
  ${textBase}
  ${textOverrides}
`;

/** Figma "Body Small" (14px / 300 / 140%) — one step below the default `Paragraph`. */
export const ParagraphSmall = styled.p<TextProps>`
  ${typographyStyles.bodySmall}
  ${textBase}
  ${textOverrides}
`;

/** Explicit-name alias of `Paragraph` (Figma "Body Medium", 15px). */
export const ParagraphMedium = styled(Paragraph)``;

/** Figma "Body Large" (22px / 300 / 160%). */
export const ParagraphLarge = styled.p<TextProps>`
  ${typographyStyles.bodyLarge}
  ${textBase}
  ${textOverrides}
`;

/** Semantic alias of `ParagraphLarge` for call sites that mean "extra-large body". */
export const ParagraphXLarge = styled(ParagraphLarge)``;

// --- Body Semibold (header-styled paragraphs) -------------------------------

/** Semibold body copy — Figma "Body Medium Semibold" (15px / 600 / 150%). */
export const ParagraphHeader = styled.p<TextProps>`
  ${typographyStyles.bodyMediumSemibold}
  ${textBase}
  ${textOverrides}
`;

/** Figma "Body Small Semibold" (14px / 600 / 20px / -0.01em). */
export const ParagraphHeaderSmall = styled.p<TextProps>`
  ${typographyStyles.bodySmallSemibold}
  ${textBase}
  ${textOverrides}
`;

/** No direct token; uses Body XSmall at semibold weight. */
export const ParagraphHeaderXSmall = styled.p<TextProps>`
  ${typographyStyles.bodyXSmall}
  font-weight: ${FONT_WEIGHT.semibold};
  ${textBase}
  ${textOverrides}
`;

/** Figma "Body Large Semibold" (22px). */
export const ParagraphHeaderLarge = styled.p<TextProps>`
  ${typographyStyles.bodyLargeSemibold}
  ${textBase}
  ${textOverrides}
`;

/** Semantic alias of `ParagraphHeaderLarge` — see `ParagraphXLarge`. */
export const ParagraphHeaderXLarge = styled(ParagraphHeaderLarge)``;

/** Document introduction — 20/15px semibold, 32/24px line height. */
export const DocumentIntroduction = styled.p<TextProps>`
  ${typographyStyles.documentIntroduction}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Generation introduction — 20px semibold / 15px bold. */
export const DocumentGenerationIntroduction = styled.p<TextProps>`
  ${typographyStyles.documentGenerationIntroduction}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Confirmation label — 14/12px semibold. */
export const DocumentConfirmationLabel = styled.p<TextProps>`
  ${typographyStyles.documentConfirmationLabel}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Disclosure approval body — 17px regular, 27px line height. */
export const DocumentApprovalBody = styled.p<TextProps>`
  ${typographyStyles.documentApprovalBody}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Document legal badge — 11px semibold / mobile bold, uppercase. */
export const DocumentLegalBadge = styled.span<TextProps>`
  ${typographyStyles.documentLegalBadge}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Document legal requirement — 14/13px semibold. */
export const DocumentLegalBody = styled.p<TextProps>`
  ${typographyStyles.documentLegalBody}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Document update timestamp — 13/12px semibold. */
export const DocumentTimestamp = styled.p<TextProps>`
  ${typographyStyles.documentTimestamp}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Regeneration description — 17/15px semibold. */
export const DocumentRegenerationBody = styled.p<TextProps>`
  ${typographyStyles.documentRegenerationBody}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Regeneration badge — 11px semibold / mobile bold. */
export const DocumentRegenerationBadge = styled.p<TextProps>`
  ${typographyStyles.documentRegenerationBadge}
  ${textBase}
  ${responsiveTextOverrides}
`;

export const PurchaseItemText = styled.p<TextProps>`
  ${typographyStyles.purchaseItem}
  ${textBase}
  ${textOverrides}
`;

/** Completion introduction — authored 18/14px semibold scale. */
export const CompletionDescription = styled.p<TextProps>`
  ${typographyStyles.completionDescription}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Completion benefits — authored 14/12px semibold scale. */
export const CompletionBenefitText = styled.p<TextProps>`
  ${typographyStyles.completionBenefit}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Compact referral navigation label. */
export const ReferralNavigationLabel = styled.p<TextProps>`
  ${typographyStyles.referralNavigationLabel}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Referral introduction — authored desktop semibold/mobile medium scale. */
export const ReferralIntro = styled.p<TextProps>`
  ${typographyStyles.referralIntro}
  ${textBase}
  ${responsiveTextOverrides}
`;

/** Referral-card copy — authored desktop semibold/mobile regular scale. */
export const ReferralCardDescription = styled.p<TextProps>`
  ${typographyStyles.referralCardDescription}
  ${textBase}
  ${responsiveTextOverrides}
`;

// --- Bold / Semibold inline strong -----------------------------------------

/**
 * Bold inline <strong> — Body Medium Semibold at weight 700. Inherits color and
 * text-align from context (doesn't set them), so it composes anywhere.
 */
export const Bold = styled.strong<TextProps>`
  ${typographyStyles.bodyMediumSemibold}
  font-weight: ${FONT_WEIGHT.bold};
  ${textBase}
  ${textOverrides}
`;

/** Semibold inline — Figma "Body Medium Semibold" (600). Inherits color/alignment. */
export const Semibold = styled.strong<TextProps>`
  ${typographyStyles.bodyMediumSemibold}
  ${textBase}
  ${textOverrides}
`;

/** Semibold inline, small — Figma "Body Small Semibold". Inherits color/alignment. */
export const SemiboldSmall = styled.strong<TextProps>`
  ${typographyStyles.bodySmallSemibold}
  ${textBase}
  ${textOverrides}
`;

// --- Overline --------------------------------------------------------------

/**
 * Uppercase overline — Figma "Overline" (13px / 700 / uppercase / 6%
 * letter-spacing). Colour inherits from :root (COLOR_APP_TEXT) unless a `color`
 * prop is passed.
 */
export const Overline = styled.span<TextProps>`
  ${typographyStyles.overline}
  ${textOverrides}
`;

/** Semibold callout copy — 17px / 160%. */
export const CalloutParagraph = styled.p<TextProps>`
  ${typographyStyles.bodyCallout}
  ${textBase}
  ${textOverrides}
`;

/** Appointment countdown numerals — 32px desktop, 28px mobile. */
export const CountdownNumber = styled.span<TextProps>`
  ${typographyStyles.attorneyCountdownNumber}
  ${textBase}
  ${textOverrides}
`;
